import { useState } from 'react';
import { alarmOf } from '../../utils/floorStatus';
import { clearAlarm, requestToolChange, finishToolChange, finishSetup } from '../../services/alarmApi';
import './hmiThemes.css';
import './fanucScreen.css';

// ── Tablet pendant ───────────────────────────────────────────────────────────
// The simplified CNC control for one machine, opened by tapping its tile on Floor Control.
//
// It shares the CRT LOOK with the Studio's FanucScreen — the same `.fanuc-*` / `.crt-*` classes and
// the same `[data-hmi-theme="fanuc"]` phosphor variables — but not its machinery. FanucScreen renders
// whatever the HMI Studio's field metadata declares (`hmiResolved.section`, widget kinds, six soft-key
// banks, a designer mode). That is right for authoring a pendant; it is the wrong shape for a booth
// operator holding an iPad, and wiring this page to the simDef field pipeline would mean a SECOND
// data path onto a floor that already has exactly one (`/machines` + `/floor/live`).
//
// So: same language, different sentence. Four pages, everything from the payload the floor already
// has, and every control sized for a thumb.
//
// WHAT IS DELIBERATELY NOT HERE: axis position. A real 0i POS page is dominated by X/Z/C readouts,
// and this payload has none — the VCNC computes block motion and focas-hero serves positions, but
// neither reaches this endpoint. Inventing plausible coordinates would make the most trustworthy-
// looking element on a demo screen the only fabricated one. POS instead shows what is real and is
// what a PROGRAM CHECK page shows anyway: the executing block, feed, override, tool and counts.

const PAGES = ['POS', 'PROG', 'TOOL', 'ALARM'];

const pad = (n, w) => String(n ?? '').padStart(w, '0');

export default function TabletPendant({ row, company, say, onClose }) {
  const { name, label, live, state, status } = row;
  const r = live?.running || null;
  const st = state || {};
  const tc = live?.toolChange || null;
  const alarm = alarmOf(live, st);

  const [page, setPage] = useState(alarm ? 'ALARM' : 'POS');
  const [busy, setBusy] = useState(null);

  const run = async (key, fn, okMsg) => {
    setBusy(key);
    try { await fn(); say?.(okMsg); }
    catch (e) { say?.(e.message || 'Failed', 'err'); }
    finally { setBusy(null); }
  };

  const program = r?.program || st.program || null;
  const execution = st.execution || (r?.isActive ? 'ACTIVE' : 'STOP');
  const mode = st.cycleMode || 'AUTO';

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 120, background: 'rgba(0,0,0,0.78)', display: 'flex', alignItems: 'stretch', justifyContent: 'center' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-hmi-theme="fanuc"
        className="fanuc-screen"
        style={{
          width: '100%', maxWidth: 860, margin: '0 auto', display: 'flex', flexDirection: 'column',
          borderRadius: 0, borderLeft: 'none', borderRight: 'none',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        {/* Titlebar — the amber strip a 0i wears. The machine's own name sits where the model badge
            does, because on this floor that is what identifies the control you are holding. */}
        <div className="fanuc-titlebar" style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
          paddingTop: 'calc(10px + env(safe-area-inset-top, 0px))', flexShrink: 0,
        }}>
          <span style={{ fontSize: 19, fontWeight: 800, letterSpacing: '0.16em' }}>FANUC</span>
          <span style={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: 700, fontFamily: "'Courier New', monospace", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {label}
          </span>
          <span className="fx-sub" style={{ fontSize: 11, fontFamily: "'Courier New', monospace", whiteSpace: 'nowrap' }}>
            SERIES 0i-MODEL F
          </span>
          <button
            type="button"
            onClick={onClose}
            style={{
              minWidth: 52, minHeight: 40, marginLeft: 4, borderRadius: 6, cursor: 'pointer',
              border: '1px solid rgba(0,0,0,0.45)', background: 'rgba(0,0,0,0.18)',
              color: 'inherit', fontFamily: 'inherit', fontSize: 13, fontWeight: 800,
              touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent',
            }}
          >
            ✕
          </button>
        </div>

        {/* Mode / execution strip */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 14, padding: '8px 14px', flexShrink: 0,
          borderBottom: '1px solid color-mix(in srgb, var(--fx-dim) 45%, transparent)',
          fontFamily: "'Courier New', monospace", fontSize: 12.5,
        }}>
          <span className="fanuc-value" style={{ fontWeight: 700 }}>{mode}</span>
          <span className="fanuc-value" style={{ fontWeight: 700 }}>{execution}</span>
          <span className="fanuc-label">{status.label}</span>
          <span style={{ flex: 1 }} />
          <span className="fanuc-label">{program ? `O${String(program).replace(/^O/i, '')}` : 'NO PRG'}</span>
        </div>

        {/* Blinking alarm line, on every page — a real control does not hide an active alarm behind
            a tab, and neither should this one. */}
        {alarm && (
          <div className={alarm.faulted || alarm.level === 'FAULT' ? 'fanuc-alarm-bad' : 'fanuc-alarm-ok'}
            style={{
              margin: '10px 14px 0', padding: '9px 12px', borderRadius: 4, borderWidth: 1, borderStyle: 'solid',
              fontFamily: "'Courier New', monospace", fontSize: 13, fontWeight: 700, flexShrink: 0,
            }}>
            {alarm.code}{alarm.message ? ` ${alarm.message}` : ''}
          </div>
        )}

        {/* Page body */}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 14 }}>
          {page === 'POS' && <PosPage r={r} st={st} tc={tc} />}
          {page === 'PROG' && <ProgPage r={r} st={st} />}
          {page === 'TOOL' && (
            <ToolPage live={live} tc={tc} busy={busy}
              onRequest={(key) => run(key, () => requestToolChange(name, key, company), `${label}: ${key} change requested`)} />
          )}
          {page === 'ALARM' && (
            <AlarmPage alarm={alarm} busy={busy === 'clear'}
              onClear={() => run('clear', () => clearAlarm(name, company), `${label}: alarm cleared`)} />
          )}
        </div>

        {/* Operator actions — phosphor buttons, only the ones this phase can actually do. A real
            pendant's keys are always present and mostly inert; a booth tablet is better off showing
            only what will respond to the tap. */}
        {(tc || status.label === 'SETUP' || alarm) && (
          <div style={{ display: 'flex', gap: 10, padding: '0 14px 12px', flexWrap: 'wrap', flexShrink: 0 }}>
            {tc && (
              <CrtAction tone="warn" busy={busy === 'ftc'}
                onPress={() => run('ftc', () => finishToolChange(name, company), `${label}: tool change finished`)}>
                FINISH TOOL CHANGE
              </CrtAction>
            )}
            {!tc && status.label === 'SETUP' && (
              <CrtAction tone="warn" busy={busy === 'fsu'}
                onPress={() => run('fsu', () => finishSetup(name, company), `${label}: setup finished`)}>
                FINISH SETUP
              </CrtAction>
            )}
            {alarm && (
              <CrtAction busy={busy === 'clear'}
                onPress={() => run('clear', () => clearAlarm(name, company), `${label}: alarm cleared`)}>
                CLEAR ALARM
              </CrtAction>
            )}
          </div>
        )}

        {/* Soft keys */}
        <div className="fanuc-softkey" style={{ display: 'flex', gap: 8, padding: 10, flexShrink: 0 }}>
          {PAGES.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPage(p)}
              className={page === p ? 'is-active' : ''}
              style={{
                flex: 1, minHeight: 52, borderRadius: 4, cursor: 'pointer',
                fontFamily: "'Courier New', monospace", fontSize: 13, fontWeight: 800, letterSpacing: '0.08em',
                touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent',
              }}
            >
              {p}
              {p === 'ALARM' && alarm && ' •'}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Pages ────────────────────────────────────────────────────────────────────

function PosPage({ r, st, tc }) {
  const partCount = r?.partCount ?? st.total_part_count ?? 0;
  const target = r?.targetQty ?? st.target_qty ?? 0;
  const cyc = Math.round(r?.cycleProgress ?? st.cycle_progress ?? 0);
  const block = r?.block ?? st.block ?? null;
  const line = r?.line ?? st.line ?? null;
  const seq = r?.sequence ?? st.sequence ?? null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
        <Big label="PART COUNT" value={partCount.toLocaleString('en-US')} sub={target ? `/ ${target.toLocaleString('en-US')}` : null} />
        <Big label="CYCLE" value={`${cyc}%`} />
        <Big label="FEEDRATE" value={st.feedrate != null ? `${st.feedrate}` : '—'} sub="mm/min" />
        <Big label="OVERRIDE" value={st.override != null ? `${st.override}%` : '—'} />
        <Big label="TOOL" value={st.tool_id != null ? `T${pad(st.tool_id, 2)}` : '—'} />
        <Big label="SEQUENCE" value={st.op_sequence != null ? `OP${st.op_sequence}` : (st.operation || '—')} />
      </div>

      <Cell label="EXECUTING BLOCK">
        {block ? (
          <div style={{ fontSize: 17, lineHeight: 1.5 }}>
            <span className="fanuc-label" style={{ marginRight: 10 }}>
              {line != null ? `L${pad(line, 4)}` : ''}{seq != null ? ` N${seq}` : ''}
            </span>
            <span className="fanuc-value">{block}</span>
          </div>
        ) : (
          <div className="fanuc-label" style={{ fontSize: 14 }}>
            {tc ? 'TOOL CHANGE IN PROGRESS' : 'NO BLOCK EXECUTING'}
          </div>
        )}
      </Cell>

      <Cell label="CYCLE PROGRESS">
        <div className="fanuc-tlbar" style={{ height: 14, borderRadius: 3, overflow: 'hidden' }}>
          <span className="tl-ok" style={{ width: `${Math.max(0, Math.min(100, cyc))}%` }} />
        </div>
      </Cell>
    </div>
  );
}

function ProgPage({ r, st }) {
  const program = r?.program || st.program || null;
  const rows = [
    ['PROGRAM', program ? `O${String(program).replace(/^O/i, '')}` : '—'],
    ['HEADER', st.program_header || '—'],
    ['PART NO.', st.part_number || '—'],
    ['PART NAME', st.part_name || '—'],
    ['WORK ORDER', r?.workOrderId || st.wo_number || '—'],
    ['OPERATION', st.operation || (st.op_sequence != null ? `OP${st.op_sequence}` : '—')],
    ['OP QTY', st.op_completed_qty != null ? `${st.op_completed_qty} / ${st.op_target_qty ?? '—'}` : '—'],
    ['BLOCK DELETE', st.blockDelete ? 'ON' : 'OFF'],
  ];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {rows.map(([k, v]) => (
        <div key={k} className="fanuc-cell" style={{
          display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12,
          padding: '11px 13px', fontFamily: "'Courier New', monospace",
        }}>
          <span className="fanuc-label" style={{ fontSize: 11 }}>{k}</span>
          <span className="fanuc-value" style={{ fontSize: 15, fontWeight: 700, textAlign: 'right', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{v}</span>
        </div>
      ))}
    </div>
  );
}

function ToolPage({ live, tc, busy, onRequest }) {
  const tools = Array.isArray(live?.tools) ? live.tools : [];
  if (!tools.length) {
    return <div className="fanuc-label" style={{ fontFamily: "'Courier New', monospace", fontSize: 14, padding: '24px 0', textAlign: 'center' }}>NO TOOL DATA</div>;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {tools.map((t) => {
        const pct = Math.max(0, Math.min(100, t.pct ?? 0));
        const cls = pct >= 100 ? 'tl-over' : pct >= 80 ? 'tl-warn' : 'tl-ok';
        const changing = tc?.toolsChanging?.includes(t.key);
        const queued = !!t.retireRequested && !changing;
        return (
          <div key={t.key} className="fanuc-cell" style={{ padding: 12, fontFamily: "'Courier New', monospace" }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, marginBottom: 8 }}>
              <span className="fanuc-value" style={{ fontSize: 16, fontWeight: 800 }}>{changing ? '↻ ' : ''}{t.key}</span>
              <span className="fanuc-value" style={{ fontSize: 14 }}>
                {t.count}{t.preset ? ` / ${t.preset}` : ''} ({pct}%)
              </span>
            </div>
            <div className="fanuc-tlbar" style={{ height: 12, borderRadius: 3, overflow: 'hidden', marginBottom: changing || queued ? 8 : 10 }}>
              <span className={cls} style={{ width: `${pct}%` }} />
            </div>
            {changing ? (
              <div className="fanuc-label" style={{ fontSize: 12 }}>CHANGING — USE FINISH TOOL CHANGE</div>
            ) : queued ? (
              <div className="fanuc-label" style={{ fontSize: 12 }}>QUEUED — FIRES AT NEXT PART</div>
            ) : (
              <CrtAction tone="warn" full busy={busy === t.key} onPress={() => onRequest(t.key)}>
                REQUEST CHANGE
              </CrtAction>
            )}
          </div>
        );
      })}
    </div>
  );
}

function AlarmPage({ alarm, busy, onClear }) {
  if (!alarm) {
    return (
      <div className="fanuc-cell fanuc-alarm-ok" style={{
        padding: '26px 14px', textAlign: 'center', fontFamily: "'Courier New', monospace",
        fontSize: 16, fontWeight: 700, borderWidth: 1, borderStyle: 'solid',
      }}>
        NO ALARM
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <Cell label="ALARM">
        <div style={{ fontFamily: "'Courier New', monospace" }}>
          <div className="fanuc-value" style={{ fontSize: 22, fontWeight: 800, letterSpacing: '0.06em' }}>{alarm.code}</div>
          {alarm.message && <div className="fanuc-value" style={{ fontSize: 15, marginTop: 6 }}>{alarm.message}</div>}
          <div className="fanuc-label" style={{ fontSize: 12, marginTop: 8 }}>
            LEVEL {alarm.level} · {alarm.faulted ? 'MACHINE HELD' : 'NON-HALTING'}
          </div>
        </div>
      </Cell>
      <CrtAction full busy={busy} onPress={onClear}>CLEAR ALARM</CrtAction>
    </div>
  );
}

// ── CRT primitives, thumb-sized ──────────────────────────────────────────────

function Big({ label, value, sub }) {
  return (
    <div className="fanuc-cell" style={{ padding: '11px 13px', fontFamily: "'Courier New', monospace" }}>
      <div className="fanuc-label" style={{ fontSize: 10, textTransform: 'uppercase' }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 4 }}>
        <span className="fanuc-value" style={{ fontSize: 24, fontWeight: 800, lineHeight: 1 }}>{value}</span>
        {sub && <span className="fanuc-label" style={{ fontSize: 12 }}>{sub}</span>}
      </div>
    </div>
  );
}

function Cell({ label, children }) {
  return (
    <div className="fanuc-cell" style={{ padding: '11px 13px', fontFamily: "'Courier New', monospace" }}>
      <div className="fanuc-label" style={{ fontSize: 10, textTransform: 'uppercase', marginBottom: 7 }}>{label}</div>
      {children}
    </div>
  );
}

// `.crt-btn` gives the phosphor look; the inline style gives it a 52px touch target, which the
// Studio's mouse-sized version (px-3 py-1.5) does not.
function CrtAction({ children, onPress, tone, busy, full }) {
  return (
    <button
      type="button"
      onClick={busy ? undefined : onPress}
      disabled={!!busy}
      className={`crt-btn ${tone === 'warn' ? 'crt-warn' : ''}`}
      style={{
        minHeight: 52, padding: '0 18px', flex: full ? '1 1 100%' : '1 1 auto',
        fontFamily: "'Courier New', monospace", fontSize: 13.5, fontWeight: 800, letterSpacing: '0.08em',
        cursor: busy ? 'default' : 'pointer', touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent',
      }}
    >
      {busy ? '…' : children}
    </button>
  );
}
